// © 2013 - 2024 Tinder, Inc., ALL RIGHTS RESERVED

import { css } from '@linaria/core';

import { themeCssVar as globalTheme } from '../../utils/theme';

export const theme = {
  transitionDuration: '250ms',
  containerWidth: globalTheme.sizing.dimensionXlarge,
  containerHeight: globalTheme.sizing.dimensionXxsmall,
  touchTargetCoarseWidth: globalTheme.sizing.dimensionXlarge,
  touchTargetCoarseHeight: globalTheme.sizing.dimensionXlarge,
  touchTargetFineWidth: globalTheme.sizing.dimensionXlarge,
  touchTargetFineHeight: globalTheme.sizing.dimensionXxsmall,
  trackRadius: globalTheme.sizing.borderRadiusXlarge,
  trackBackgroundUnselected: globalTheme.color.backgroundSwitchDefaultTrackUnselected,
  trackBackgroundSelected: globalTheme.color.backgroundSwitchDefaultTrackSelected,
  trackBackgroundDisabled: globalTheme.color.backgroundSwitchDefaultTrackDisabled,
  trackBackgroundReadOnly: globalTheme.color.backgroundSwitchDefaultTrackDisabled,
  trackBorderWidth: globalTheme.sizing.borderWidthThin,
  trackBorder: globalTheme.color.borderSwitchDefaultTrackUnselected,
  trackBorderSelected: globalTheme.color.borderSwitchDefaultTrackSelected,
  trackBorderDisabled: globalTheme.color.borderSwitchDefaultTrackDisabled,
  trackBorderReadOnly: globalTheme.color.borderSwitchDefaultTrackDisabled,
  trackOutlineWidth: globalTheme.sizing.borderWidthRegular,
  trackOutlineOffset: globalTheme.sizing.paddingXxsmall,
  knobDiameter: globalTheme.sizing.dimensionXxsmall,
  knobBackground: globalTheme.color.backgroundSwitchDefaultKnobUnselected,
  knobBackgroundSelected: globalTheme.color.backgroundSwitchDefaultKnobSelected,
  knobBackgroundDisabled: globalTheme.color.backgroundSwitchDefaultKnobDisabled,
  knobBackgroundReadOnly: globalTheme.color.backgroundSwitchDefaultKnobDisabled,
  knobBorderWidth: globalTheme.sizing.borderWidthThin,
  knobBorder: globalTheme.color.borderSwitchDefaultKnobUnselected,
  knobBorderSelected: globalTheme.color.borderSwitchDefaultKnobSelected,
  knobBorderDisabled: globalTheme.color.borderSwitchDefaultKnobDisabled,
  knobBorderReadOnly: globalTheme.color.borderSwitchDefaultKnobDisabled,
  focusBackground: globalTheme.color.borderFocusDefault,
  opacityDisabled: 0.8,
  overlay: globalTheme.color.interactiveButtonPrimary,
  overlayDisabled: 'transparent',
};

const cssVariable = {
  translateXOff: `--translate-x-off`,
  translateXOn: `--translate-x-on`,
  touchTargetWidth: `--touch-target-width`,
  touchTargetHeight: `--touch-target-height`,
  accentColor: `--tui-switch-accent-color`,
};

const container = css`
  display: inline-block;
  position: relative;
  width: ${theme.containerWidth};
  height: ${theme.containerHeight};

  @media (pointer: fine) {
    ${cssVariable.touchTargetWidth}: ${theme.touchTargetFineWidth};
    ${cssVariable.touchTargetHeight}: ${theme.touchTargetFineHeight};
  }

  @media (pointer: coarse) {
    ${cssVariable.touchTargetWidth}: ${theme.touchTargetCoarseWidth};
    ${cssVariable.touchTargetHeight}: ${theme.touchTargetCoarseHeight};
  }

  > input[type='checkbox'] {
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
    transform: translate(-50%, -50%);
    margin: 0;
    padding: 0;
    opacity: 0;
    box-sizing: border-box;

    min-width: var(${cssVariable.touchTargetWidth});
    min-height: var(${cssVariable.touchTargetHeight});
  }

  &:not([data-disabled]):not([data-readonly]) > input[type='checkbox'] {
    cursor: pointer;
  }
`;

const track = css`
  width: 100%;
  height: 100%;
  border-radius: ${theme.trackRadius};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${theme.trackBackgroundUnselected};
  border: ${theme.trackBorderWidth} solid ${theme.trackBorder};
  box-sizing: border-box;
  pointer-events: none;
  transition: background-color ${theme.transitionDuration} ease;
  outline-width: ${theme.trackOutlineWidth};
  outline-offset: ${theme.trackOutlineOffset};
  outline-color: ${theme.focusBackground};
  outline-style: none;

  ${`${cssVariable.accentColor}: ${theme.trackBackgroundSelected};`}

  .${container}[data-disabled] & {
    background-color: ${theme.trackBackgroundDisabled};
    border-color: ${theme.trackBorderDisabled};
  }

  .${container}[data-readonly] & {
    background-color: ${theme.trackBackgroundReadOnly};
    border-color: ${theme.trackBorderReadOnly};
  }

  .${container}[data-checked] & {
    background-color: var(${cssVariable.accentColor}, ${theme.trackBackgroundSelected});
    border-color: var(${cssVariable.accentColor}, ${theme.trackBorderSelected});
  }

  .${container}[data-focused] & {
    outline-style: solid;
  }
`;

const knob = css`
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  width: ${theme.knobDiameter};
  height: ${theme.knobDiameter};
  background-color: ${theme.knobBackground};
  border: ${theme.knobBorderWidth} solid ${theme.knobBorder};
  box-sizing: border-box;
  border-radius: 50%;
  transition: transform ${theme.transitionDuration} ease,
    background-color ${theme.transitionDuration} ease, border-color ${theme.transitionDuration} ease;
  transform: translateX(var(${cssVariable.translateXOff}, 0rem));

  .${container}[data-checked] & {
    transform: translateX(
      var(${cssVariable.translateXOn}, calc(${theme.containerWidth} - ${theme.knobDiameter}))
    );
    border-color: var(${cssVariable.accentColor}, ${theme.knobBorderSelected});
  }

  .${container}[data-disabled] & {
    border-color: ${theme.knobBorderDisabled};
  }

  .${container}[data-readonly] & {
    border-color: ${theme.knobBorderReadOnly};
  }

  &,
  [dir='ltr'] & {
    ${cssVariable.translateXOff}: 0rem;
    ${cssVariable.translateXOn}: calc(${theme.containerWidth} - ${theme.knobDiameter});
  }

  [dir='rtl'] & {
    ${cssVariable.translateXOff}: calc(${theme.containerWidth} - ${theme.knobDiameter});
    ${cssVariable.translateXOn}: 0rem;
  }
`;

const overlay = css`
  display: block;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
  overflow: hidden;
  border-radius: ${theme.trackRadius};
  opacity: 0;
  background-color: ${theme.overlay};

  .${container}[data-disabled] &,
  .${container}[data-readonly] & {
    background-color: ${theme.overlayDisabled};
  }

  @media (hover: hover) and (pointer: fine) {
    .${container}:hover & {
      opacity: 1;
    }
  }

  .${container}:active & {
    opacity: 1;
  }
`;

const styles = {
  container,
  track,
  knob,
  overlay,
};

export default styles;
